import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChange,
} from "@angular/core";
import { HdOption } from "../model/common-type";
import {
  HdDetailFormFieldType,
  HdDetailFormImage,
} from "./hd-detail-form.service";

/** 附件缩略图最多直接展示张数，超出末张蒙版 +n */
const MAX_VISIBLE_IMAGES = 4;

@Component({
  selector: "hd-detail-form",
  templateUrl: "./hd-detail-form.component.html",
  styleUrls: ["./hd-detail-form.component.less"],
})
export class HdDetailFormComponent implements OnInit {
  @Input() formCols: Array<HdOption> = new Array();
  /** 一行列数，默认固定 4 列（不再按屏幕宽度自适应） */
  @Input() nzColumn:
    | number
    | {
        xxl?: number;
        xl?: number;
        lg?: number;
        md?: number;
        sm?: number;
        xs?: number;
      } = 4;

  /** 附件图片点击回调 */
  @Output() imageClick = new EventEmitter<{
    image: HdDetailFormImage;
    path: string;
    index: number;
  }>();

  HdDetailFormFieldType = HdDetailFormFieldType;

  /** 大图预览 */
  previewVisible = false;
  previewImagePath = "";
  previewTitle = "图片预览";
  /** 当前预览所属字段的图片列表（用于翻页） */
  previewImages: Array<HdDetailFormImage> = [];
  previewIndex = 0;

  /** 附件字段图片缓存（避免模板里反复 getImageList 导致 *ngFor 重建、点击丢失） */
  private imageListCache = new Map<HdOption, Array<HdDetailFormImage>>();
  private displayListCache = new Map<HdOption, Array<HdDetailFormImage>>();

  constructor() {}

  get canPreviewPrev(): boolean {
    return this.previewIndex > 0;
  }

  get canPreviewNext(): boolean {
    return this.previewIndex < this.previewImages.length - 1;
  }

  ngOnInit() {
    if (this.formCols && this.formCols.length > 0) {
      this.formCols = this.formCols.filter((item) => !item.hide);
    }
    this.refreshImageCache();
  }

  ngOnChanges(changes: SimpleChange) {
    if (
      changes["formCols"] &&
      JSON.stringify(changes["formCols"].currentValue) !==
        JSON.stringify(changes["formCols"].previousValue)
    ) {
      if (this.formCols && this.formCols.length > 0) {
        this.formCols = this.formCols.filter((item) => !item.hide);
      }
      this.refreshImageCache();
    }
  }

  isAccessory(col: HdOption): boolean {
    return !!(
      col &&
      (col.type === HdDetailFormFieldType.Accessory || col.type === "accessory")
    );
  }

  private refreshImageCache(): void {
    this.imageListCache.clear();
    this.displayListCache.clear();
    (this.formCols || []).forEach((col) => {
      if (this.isAccessory(col)) {
        this.imageListCache.set(col, this.parseImageList(col));
      }
    });
  }

  /**
   * 解析附件图片列表。value 支持：
   * - string / string[]（图片地址）
   * - { name?, path? / url? }[]
   */
  private parseImageList(col: HdOption): Array<HdDetailFormImage> {
    const value = col && col.value;
    if (value == null || value === "") {
      return [];
    }
    const list = Array.isArray(value) ? value : [value];
    const result: Array<HdDetailFormImage> = [];
    list.forEach((item) => {
      if (item == null || item === "") {
        return;
      }
      if (typeof item === "string") {
        result.push({ path: item });
        return;
      }
      const path = item.path || item.url || item.address || "";
      if (!path) {
        return;
      }
      result.push({
        name: item.name || item.fileName,
        path: String(path),
      });
    });
    return result;
  }

  getImageList(col: HdOption): Array<HdDetailFormImage> {
    if (!this.imageListCache.has(col)) {
      this.imageListCache.set(col, this.parseImageList(col));
    }
    return this.imageListCache.get(col) || [];
  }

  hasImages(col: HdOption): boolean {
    return this.getImageList(col).length > 0;
  }

  /** 实际渲染的缩略图（最多 4 张） */
  getDisplayImages(col: HdOption): Array<HdDetailFormImage> {
    if (this.displayListCache.has(col)) {
      return this.displayListCache.get(col);
    }
    const display = this.getImageList(col).slice(0, MAX_VISIBLE_IMAGES);
    this.displayListCache.set(col, display);
    return display;
  }

  /** 未展示张数（末张蒙版 +n） */
  getMoreCount(col: HdOption): number {
    const total = this.getImageList(col).length;
    return total > MAX_VISIBLE_IMAGES ? total - MAX_VISIBLE_IMAGES : 0;
  }

  getImagePath(image: HdDetailFormImage): string {
    return (image && (image.path || image.url)) || "";
  }

  trackByImage(index: number, image: HdDetailFormImage): string {
    return (image && (image.path || image.url)) || String(index);
  }

  onImageClick(
    col: HdOption,
    image: HdDetailFormImage,
    index: number,
    event?: Event
  ): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const path = this.getImagePath(image);
    this.imageClick.emit({ image, path, index });
    if (!path) {
      return;
    }
    this.previewImages = this.getImageList(col).slice();
    this.previewIndex = index >= 0 ? index : 0;
    this.applyPreviewImage();
    setTimeout(() => {
      this.previewVisible = true;
    });
  }

  previewPrev(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.canPreviewPrev) {
      return;
    }
    this.previewIndex -= 1;
    this.applyPreviewImage();
  }

  previewNext(event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!this.canPreviewNext) {
      return;
    }
    this.previewIndex += 1;
    this.applyPreviewImage();
  }

  private applyPreviewImage(): void {
    const image = this.previewImages[this.previewIndex];
    const path = this.getImagePath(image);
    this.previewImagePath = path;
    this.previewTitle =
      (image && image.name) || "图片" + (this.previewIndex + 1);
  }

  closePreview(): void {
    this.previewVisible = false;
    this.previewImages = [];
    this.previewIndex = 0;
    this.previewImagePath = "";
  }

  /** HdOption.width：默认 1，最大 4 */
  getColSpan(col: HdOption): number {
    const width = col && col.width != null ? Number(col.width) : 1;
    if (!width || width < 1 || isNaN(width)) {
      return 1;
    }
    return width > 4 ? 4 : Math.floor(width);
  }

  triggerEvent(fn: any) {
    if (fn) {
      fn(event);
    }
  }

  /** 点击编辑笔，回调父组件传入的 edit */
  onEditClick(event: Event, col: HdOption): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (col && col.edit) {
      col.edit(col);
    }
  }
}
